import { Avatar, Flex, Tooltip, Typography } from 'antd'
import type { CSSProperties, ReactNode } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import { useHasWorkspacePermissions } from 'src/hooks'
import { composeDisplayName } from 'src/utils/string'
import { WorkspacePermissionCode } from 'src/utils/workspacePermissions'

import { pickAvatarColor } from './avatarColor'

const { Text, Link } = Typography

type Size = 'sm' | 'md' | 'lg'

type SizeSpec = {
  avatar: number
  avatarFont: number
  name: number
  meta: number
}

const SIZE_MAP: Record<Size, SizeSpec> = {
  sm: { avatar: 22, avatarFont: 10, name: 12, meta: 11 },
  md: { avatar: 28, avatarFont: 12, name: 13, meta: 11 },
  lg: { avatar: 38, avatarFont: 14, name: 14, meta: 12 }
}

type Props = {
  firstName?: string | null
  lastName?: string | null
  /** Falls back to first 2 letters of the email handle when no name parts are present. */
  email?: string | null
  /** Optional secondary line under the name (typically email, role or title). */
  meta?: string | null
  size?: Size
  /**
   * Avatar background. When omitted, a deterministic palette pick is derived
   * from the user's name/email — every person gets a consistent color across
   * the app, all chosen to pass WCAG AA against white initials.
   */
  color?: string
  /** Render the name as a Typography.Link (with click handler). */
  onClick?: () => void
  /**
   * Render the name as a real react-router `Link` to this path (proper anchor:
   * cmd/middle-click, open-in-new-tab). Takes precedence over `onClick`.
   */
  to?: string
  /** If true, hide the meta line even when provided. */
  hideMeta?: boolean
  /** Show a copy button on the meta line (copies the meta/email text). */
  copyableMeta?: boolean
  /** Use the avatar's photo URL if present. */
  photoUrl?: string | null
  /** Override row style (cell padding, etc.). */
  style?: CSSProperties
  /** Trailing slot (e.g. status / MFA badge). */
  trailing?: ReactNode
}

/** 2-letter initials when both name parts are present; otherwise 1-2 letters from
 *  whatever is available. Falls back to email handle. */
export const computeInitials = (
  firstName?: string | null,
  lastName?: string | null,
  email?: string | null
): string => {
  const f = (firstName ?? '').trim()
  const l = (lastName ?? '').trim()
  if (f && l) return `${f[0]}${l[0]}`.toUpperCase()
  if (f) return f.slice(0, 2).toUpperCase()
  if (l) return l.slice(0, 2).toUpperCase()
  const handle = (email ?? '').split('@')[0]
  if (handle) {
    return handle.length >= 2
      ? `${handle[0]}${handle[1]}`.toUpperCase()
      : handle[0].toUpperCase()
  }
  return '?'
}

/**
 * Compact "user cell" used inside tables, drawers and member lists. Pair the
 * avatar with the person's display name and (optionally) a meta line for
 * email/role/title. Consistent sizing so different surfaces don't drift.
 */
export const UserCell = ({
  firstName,
  lastName,
  email,
  meta,
  size = 'sm',
  color,
  onClick,
  to,
  hideMeta,
  copyableMeta,
  photoUrl,
  style,
  trailing
}: Props) => {
  const spec = SIZE_MAP[size]
  // Both link variants lead to a member's page, which `users.view` opens. With
  // the code missing the name stays plain text rather than a dead end.
  const mayViewMembers = useHasWorkspacePermissions(
    WorkspacePermissionCode.USERS_VIEW
  )
  const linkTo = mayViewMembers ? to : undefined
  const handleClick = mayViewMembers ? onClick : undefined

  const name = composeDisplayName(firstName, lastName, email)
  const initials = computeInitials(firstName, lastName, email)
  const metaText = !hideMeta ? (meta ?? email ?? undefined) : undefined
  // Stable seed: prefer composed name so re-orders don't shuffle colors; email
  // as fallback when only the email is known.
  const avatarBackground = color ?? pickAvatarColor(name || email || initials)

  return (
    <Flex
      align="center"
      gap={9}
      style={{
        minWidth: 0,
        cursor: handleClick ? 'pointer' : 'default',
        ...style
      }}
      onClick={handleClick}
    >
      <Avatar
        size={spec.avatar}
        src={photoUrl ?? undefined}
        style={{
          background: avatarBackground,
          color: '#fff',
          fontSize: spec.avatarFont,
          fontWeight: 600,
          flex: '0 0 auto',
          lineHeight: `${spec.avatar}px`
        }}
      >
        {initials}
      </Avatar>
      <Flex vertical style={{ minWidth: 0, flex: 1, lineHeight: 1.22 }}>
        {linkTo ? (
          // Real anchor (react-router), styled to match the Typography.Link
          // look used for the click-handler variant.
          <RouterLink
            to={linkTo}
            title={name}
            style={{ minWidth: 0, textDecoration: 'none' }}
          >
            <Text
              strong
              style={{ fontSize: spec.name, color: 'var(--ant-color-link)' }}
              ellipsis={{ tooltip: false }}
            >
              {name}
            </Text>
          </RouterLink>
        ) : handleClick ? (
          <Link strong style={{ fontSize: spec.name }} ellipsis>
            {name}
          </Link>
        ) : (
          <Tooltip title={name.length > 24 ? name : undefined}>
            <Text
              strong
              style={{ fontSize: spec.name }}
              ellipsis={{ tooltip: false }}
            >
              {name}
            </Text>
          </Tooltip>
        )}
        {metaText ? (
          copyableMeta ? (
            // `display: contents` keeps the layout identical while still
            // catching the copy-icon click, so copying doesn't bubble up to the
            // row's `onClick` (e.g. navigation).
            <span
              style={{ display: 'contents' }}
              onClick={(e) => e.stopPropagation()}
            >
              <Text
                type="secondary"
                style={{ fontSize: spec.meta }}
                ellipsis={{ tooltip: metaText }}
                copyable={{ text: metaText }}
              >
                {metaText}
              </Text>
            </span>
          ) : (
            <Text
              type="secondary"
              style={{ fontSize: spec.meta }}
              ellipsis={{ tooltip: metaText }}
            >
              {metaText}
            </Text>
          )
        ) : null}
      </Flex>
      {trailing}
    </Flex>
  )
}
